import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { SaveOutlined } from '@mui/icons-material';
import { StyledTextField, TitleAndButton } from './styles';
import useAlertStore from '@/stores/alertStore';
import { useUpdateNote } from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import { DEFAULT_SUMMARY_PLACEHOLDER } from '../../constants';

interface SummaryProps {
  content: string;
  isOwner: boolean;
  isProcessing?: boolean;
  isLoadingNote?: boolean;
  /** Raw summary from the note (null/empty if no summary exists) */
  rawSummary?: string | null;
}

const Summary: FC<SummaryProps> = ({ content, isOwner, isLoadingNote, isProcessing, rawSummary }) => {
  const { noteId } = useParams();
  const showAlert = useAlertStore((state) => state.showAlert);
  const { mutateAsync: updateNote } = useUpdateNote();
  const { userToken } = useAuthStore();
  const showEditButton = !isProcessing && !isLoadingNote && isOwner;

  const [summary, setSummary] = useState(content || '');
  const [isEditing, setIsEditing] = useState(false);

  const handleEditOrSave = async () => {
    // Compare against raw persisted summary (not display content with placeholder)
    const persistedSummary = rawSummary ?? '';
    if (isEditing && noteId && summary !== persistedSummary) {
      await updateNote(
        {
          headers: { authorization: `Bearer ${userToken}` },
          pathParams: { noteId },
          body: {
            summary: summary,
          },
        },
        {
          onError: () => {
            showAlert('Something went wrong while updating the summary. Try again later.', 'error');
          },
        },
      );
    }
    // When entering edit mode, clear the default placeholder text only if note has no real summary
    if (!isEditing && !rawSummary && summary === DEFAULT_SUMMARY_PLACEHOLDER) {
      setSummary('');
    }
    setIsEditing(!isEditing);
  };

  useEffect(() => {
    if (content) {
      setSummary(content);
    }
  }, [content]);

  return (
    <Box sx={{ maxWidth: '800px', width: '100%' }}>
      <TitleAndButton>
        <Typography
          variant="button"
          component="div"
          sx={{
            textTransform: 'none',
            fontSize: '13px !important',
            color: 'text.primary',
            fontWeight: 600,
          }}
        >
          <Box mt={0}>Summary</Box>
        </Typography>
        {showEditButton && (
          <Button
            variant="text"
            color="primary"
            startIcon={isEditing ? <SaveOutlined /> : <EditOutlinedIcon />}
            onClick={handleEditOrSave}
          >
            {isEditing ? 'Save Changes' : 'Edit'}
          </Button>
        )}
      </TitleAndButton>
      {isLoadingNote ? (
        <Skeleton variant="rounded" height="50px" sx={{ borderRadius: '22px', marginTop: 1, width: '100%' }} />
      ) : (
        <StyledTextField
          id="summary-textfield"
          disabled={!isEditing}
          hiddenLabel
          multiline
          fullWidth
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
        />
      )}
    </Box>
  );
};

export default Summary;
